import type { TranslationKey } from "@privacy/translations";
import { useTranslation } from "@privacy/translations";
import type { GridColDef, GridFilterInputValueProps } from "@mui/x-data-grid-premium";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { useMemo, type FC } from "react";
import {
  PersonIcon3,
  RegistrationControllerIcon2,
  RegistrationProcessorIcon2,
  RegistrationFromCustomerLibraryIcon2,
  DpiaIcon3,
  LayoutDashboardIcon,
  TenantIcon3,
  FilesIcon,
} from "../../shared/icons";
import {
  prefetchRegistrationById,
  prefetchRegistrationForm,
  prefetchRegistrationsGridView,
  prefetchRegistrationUsersWithAccessCount,
  useGetRegistrationForm,
  useGetRegistrationsGridView,
  useUpdateRegistrationsGridViewStringifiedFilters,
} from "../../api/registrations";
import { DynamicGrid } from "../../dynamicGrid";
import { scopeRegistrationsToController, useControllerFilter } from "./ControllerFilter";
import type { SidePanelGroup } from "../../dynamicGrid/SidePanels";
import { EmptyCell } from "../../dynamicGrid/columnDefinitions";
import { DynamicShared } from "../../dynamicShared";
import { ConnectedTenantEntry, TenantControllerName, connectedTenantsText } from "../../shared/ConnectedTenantsList";
import { DynamicForm } from "../../dynamicForm";
import {
  IS_SHARED_FROM_CUSTOMER_FIELD,
  REGISTRATION_STATUS_VARIANTS,
  type GridViewRegistration,
} from "../../api/registrations.types";
import { systemsQueryOptions } from "../../api/systems";
import { useGetUsersFromCurrentOrganisation } from "../../api/users";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { usePrivacyDocumentTitle } from "../../shared/usePrivacyDocumentTitle";
import { EmptyState } from "../../shared/EmptyState";
import { useGetDpias } from "../../api/dpia";
import { useCurrentCustomer, useCurrentOrganisation } from "../../root/CurrentOrganisationProvider";
import { customerOrganisationNames } from "../../enterpriseCustomer/enterpriseCustomer";
import { HoverPopover, useHoverPopover } from "../../shared/HoverPopover";
import { usePermission } from "../../shared/permissions";
import { personalDataControllersDisplayListQueryOptions } from "../../api/personalDataControllers";
import { useGetTenantsHierarchyList } from "../../api/tenants";
import { trackEvent } from "../../shared/Snowplow";
import { selectPartnerOptionsQueryOptions } from "../../api/partners";
import {
  REGISTRATION_METADATA_FIELDS,
  REGISTRATION_PROCESSOR_METADATA_FIELDS,
  processorOf,
  type ConnectedTenant,
} from "../../api/registrations.types";
import { RegistrationToolbar } from "./RegistrationGridToolbar";
import { RegistrationStatusChip } from "../../shared/RegistrationStatus";
import type { RegistrationVariant } from "./RegistrationVariantProvider";
import { RegistrationVariantProvider, useRegistrationVariant } from "./RegistrationVariantProvider";
import { RegistrationSidePanel } from "./RegistrationSidePanel/RegistrationSidePanel";
import { RISK_TAGS } from "./RegistrationSidePanel/RisksFactors";
import { CustomerLibraryPanel } from "./RegistrationSidePanel/CustomerLibraryPanel";
import { RegistrationInlineFormDrawer, RegistrationMultiStepFormDrawer } from "./RegistrationDrawer/RegistrationDrawer";
import { DpiaRecommendedSubDrawer } from "./RegistrationDrawer/DpiaRecommendedSubDrawer";
import { UsersWithAccessSubDrawer } from "./RegistrationDrawer/UserWithAccessSubDrawer";
import { RegistrationHistoryDrawer } from "./RegistrationDrawer/RegistrationHistoryDrawer";
import { RegistrationActionMenu } from "./RegistrationDrawer/RegistrationDrawerHeader";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useQuery } from "@tanstack/react-query";

const RegistrationsGridPage: FC<{ variant: RegistrationVariant }> = ({ variant }) => {
  return (
    <RegistrationVariantProvider variant={variant}>
      <RegistrationsGridPageWithVariant />
    </RegistrationVariantProvider>
  );
};

const RegistrationsGridPageWithVariant = () => {
  const { t } = useTranslation();
  const variant = useRegistrationVariant();
  const currentOrganisation = useCurrentOrganisation();
  const currentCustomer = useCurrentCustomer();
  usePrivacyDocumentTitle(`RegistrationGrid.${variant}.Title`);
  const registrationsGridView = useGetRegistrationsGridView(variant);
  const registrationsForm = useGetRegistrationForm(variant);
  const { mutate: updateStringifiedFilters } = useUpdateRegistrationsGridViewStringifiedFilters();
  const controllerFilter = useControllerFilter(registrationsGridView.data?.stringifiedFilters);

  const registrations = registrationsGridView.data?.registrations;

  const visibleRegistrations = useMemo(
    () => registrations && scopeRegistrationsToController(registrations, controllerFilter.selectedControllerId),
    [registrations, controllerFilter.selectedControllerId]
  );
  const systemsQueryResult = useQuery(systemsQueryOptions());
  const dpiasQueryResult = useGetDpias({ enabled: currentOrganisation.dpiaEnabled });
  const usersQueryResult = useGetUsersFromCurrentOrganisation();
  const multiTenantsQueryResult = useGetTenantsHierarchyList();
  const personalDataControllersQueryResult = useQuery(personalDataControllersDisplayListQueryOptions());
  const partnersQueryResult = useQuery(selectPartnerOptionsQueryOptions());
  const { navigate, paths } = usePrivacyNavigate();
  const { hasPermission } = usePermission();
  const isEnterpriseController = variant === "Controller" && currentCustomer.customerKind === "enterprise";

  return (
    <DynamicGrid.Grid
      rowsData={visibleRegistrations}
      form={registrationsForm.data}
      getRowId={getRowId}
      asyncQuestionCardData={useMemo(
        () => ({
          systemsQueryData: systemsQueryResult.data,
          usersQueryData: usersQueryResult.data,
          dpiasQueryData: dpiasQueryResult.data,
          personalDataControllersQueryData: personalDataControllersQueryResult.data,
          multiTenantsQueryData: multiTenantsQueryResult.data,
          partnersQueryData: partnersQueryResult.data,
          enterpriseCustomerOrganisationsQueryData: customerOrganisationNames(currentCustomer),
        }),
        [
          systemsQueryResult.data,
          usersQueryResult.data,
          dpiasQueryResult.data,
          personalDataControllersQueryResult.data,
          multiTenantsQueryResult.data,
          partnersQueryResult.data,
          currentCustomer,
        ]
      )}
      savedFiltersProps={{
        stringifiedFilters: registrationsGridView.data?.stringifiedFilters,
        extraState: { controllerId: controllerFilter.selectedControllerId },
        onReset: controllerFilter.followSavedView,
        onChange: stringifiedFilters => {
          controllerFilter.onSavedFiltersChange(stringifiedFilters);
          trackEvent.changeRegistrationSavedFilters();
          updateStringifiedFilters(stringifiedFilters);
        },
      }}
      onRowSelect={id => navigate(paths.registrationsInline({ id, variant }))}
      defaultSort={{ field: REGISTRATION_METADATA_FIELDS.UPDATED_AT, sort: "desc" }}
      Toolbar={RegistrationToolbar}
      Footer={RegistrationGridFooter}
      mainColumnProps={{
        headerName: t("RegistrationGrid.NameHeader"),
        renderLabel: row => row.name,
        renderIcon: ({ iconProps, row }) => {
          if (row.isSharedFromCustomer) return <RegistrationFromCustomerLibraryIcon2 {...iconProps} />;
          const Icon = variant === "Controller" ? RegistrationControllerIcon2 : RegistrationProcessorIcon2;
          return <Icon {...iconProps} />;
        },
      }}
      getColumns={questionCardColumns => [
        statusColumn(t),
        DynamicGrid.dateTimeColumn({
          headerName: t("RegistrationGrid.CreatedAtHeader"),
          field: REGISTRATION_METADATA_FIELDS.CREATED_AT,
        }),
        DynamicGrid.dateTimeColumn({
          headerName: t("RegistrationGrid.UpdatedAtHeader"),
          field: REGISTRATION_METADATA_FIELDS.UPDATED_AT,
        }),
        ...(isEnterpriseController ? [sharedFromCustomerColumn(t)] : []),
        ...(variant === "Processor" && currentCustomer.customerKind !== "enterprise"
          ? [processorColumn({ headerName: t("RegistrationGrid.Connected.Processor.Header") })]
          : []),
        connectedTenantColumn({
          headerName: t(`RegistrationGrid.${variant}.ConnectedTenantHeader`),
          width: variant === "Controller" && currentCustomer.customerKind !== "enterprise" ? 360 : 240,
        }),
        responsibleColumn({ headerName: t("RegistrationGrid.ResponsibleHeader") }),
        usersWithAccessColumn({ headerName: t("RegistrationGrid.UsersWithAccessHeader") }),
        ...(variant === "Controller" ? [dpiaColumn(t)] : []),
        ...questionCardColumns,
      ]}
      NoRowsOverlay={NoRegistrationOverlay}
      sidePanelProps={{
        panelGroups: [
          ...(isEnterpriseController && hasPermission("nova-forms-api:registration:create")
            ? [
                {
                  mainPanelKey: CUSTOMER_LIBRARY_PANEL_KEY,
                  subPanelKeys: [],
                  renderTabIcon: iconProps => <FilesIcon {...iconProps} />,
                  tabLabel: t("RegistrationGrid.SidePanels.CustomerLibrary.Title"),
                  panelWidth: "680px",
                } satisfies SidePanelGroup,
              ]
            : []),
          {
            mainPanelKey: "overview",
            subPanelKeys: [...REGISTRATION_STATUS_VARIANTS, ...RISK_TAGS],
            renderTabIcon: iconProps => <LayoutDashboardIcon {...iconProps} />,
            tabLabel: t("RegistrationGrid.SidePanels.Overview.TabLabel"),
          },
        ],
        renderContent: ({ activePanelKey, activePanelGroup }) =>
          activePanelKey === CUSTOMER_LIBRARY_PANEL_KEY ? (
            <CustomerLibraryPanel />
          ) : (
            <RegistrationSidePanel
              activePanelKey={activePanelKey}
              activeSidePanelGroup={activePanelGroup}
              data={visibleRegistrations}
              form={registrationsForm.data}
            />
          ),
      }}
      texts={{ searchFieldPlaceholder: t("RegistrationGrid.SearchFieldLabel") }}
      renderActionMenu={({ row, selectedRowId }) => (
        <RegistrationActionMenu
          registrationId={row.registrationId}
          onDeleted={() => {
            if (selectedRowId === row.registrationId) navigate(paths.registrationsGrid({ variant }));
          }}
          onCopied={newRegistrationId => navigate(paths.registrationsInline({ id: newRegistrationId, variant }))}
        />
      )}
    />
  );
};

const CUSTOMER_LIBRARY_PANEL_KEY = "customer-library";

const getRowId = (row: GridViewRegistration) => row.registrationId;

const RegistrationGridFooter = () => <DynamicGrid.Footer numberOfRowsTextKey="RegistrationGrid.NumberOfRows" />;

const NoRegistrationOverlay = () => {
  return <EmptyState labelKey="RegistrationGrid.NoRows" />;
};

const statusColumn = (t: (translation: TranslationKey) => string): GridColDef<GridViewRegistration> => {
  return {
    ...DynamicGrid.requiredColDef({ headerName: t("RegistrationGrid.StatusHeader"), field: "status" }),
    width: 180,
    filterOperators: [
      {
        value: "is",
        getApplyFilterFn: filterItem => {
          if (!filterItem.field || !filterItem.value || !filterItem.operator) return null;
          return value => value === filterItem.value;
        },
        InputComponent: StatusColumnFilterInput,
      },
    ],
    /** The value getter value is used for sorting and searching */
    valueGetter: (_, row) => t(`RegistrationStatus.${row.status.status}`),
    renderCell: ({ row }) => (
      <DynamicGrid.Cell>
        <RegistrationStatusChip status={row.status.status} />
      </DynamicGrid.Cell>
    ),
  };
};

const StatusColumnFilterInput = (props: GridFilterInputValueProps) => {
  const { t } = useTranslation();
  const { item, applyValue, focusElementRef } = props;

  return (
    <Select
      fullWidth
      size="small"
      value={item.value ?? ""}
      onChange={event => applyValue({ ...item, value: event.target.value })}
      inputRef={focusElementRef}
    >
      <MenuItem hidden sx={{ display: "none" }} value="">
        {t("Filter.DefaultValue")}
      </MenuItem>
      <MenuItem value={t("RegistrationStatus.draft")}>{t("RegistrationStatus.draft")}</MenuItem>
      <MenuItem value={t("RegistrationStatus.review")}>{t("RegistrationStatus.review")}</MenuItem>
      <MenuItem value={t("RegistrationStatus.done")}>{t("RegistrationStatus.done")}</MenuItem>
      <MenuItem value={t("RegistrationStatus.completion")}>{t("RegistrationStatus.completion")}</MenuItem>
      <MenuItem value={t("RegistrationStatus.audit")}>{t("RegistrationStatus.audit")}</MenuItem>
    </Select>
  );
};

const VISIBLE_TENANTS_IN_GRID = 2;

const connectedTenantColumn = ({
  headerName,
  width,
}: {
  headerName: string;
  width: number;
}): GridColDef<GridViewRegistration> => ({
  ...DynamicGrid.requiredColDef({ headerName, field: REGISTRATION_METADATA_FIELDS.CONNECTED_TENANT }),
  width,
  valueGetter: (_, row) => (row.isSharedFromCustomer ? "" : connectedTenantsText(row.connectedTenants)),
  renderCell: ({ row }) =>
    row.isSharedFromCustomer ? <EmptyCell /> : <ConnectedTenantsCell tenants={row.connectedTenants} />,
});

const ConnectedTenantsCell = ({ tenants }: { tenants: GridViewRegistration["connectedTenants"] }) => {
  const variant = useRegistrationVariant();
  const currentCustomer = useCurrentCustomer();

  return (
    <DynamicGrid.Cell withExtraSpacingTop={false}>
      <DynamicGrid.LimitVisibleItems
        items={tenants}
        numberOfVisibleItems={VISIBLE_TENANTS_IN_GRID}
        stacked
        renderItem={tenant => (
          <ConnectedTenantEntry key={tenant.id} tenant={tenant}>
            {currentCustomer.customerKind !== "enterprise" &&
              variant === "Controller" &&
              tenant.personalDataController && (
                <TenantControllerName>{tenant.personalDataController.name}</TenantControllerName>
              )}
          </ConnectedTenantEntry>
        )}
      />
    </DynamicGrid.Cell>
  );
};

const processorColumn = ({ headerName }: { headerName: string }): GridColDef<GridViewRegistration> => ({
  ...DynamicGrid.requiredColDef({
    headerName,
    field: REGISTRATION_PROCESSOR_METADATA_FIELDS.CONNECTED_PERSONAL_DATA_CONTROLLER,
  }),
  width: 240,
  valueGetter: (_, row) => processorOf(row)?.name ?? "",
  renderCell: ({ row }) => <ProcessorCell processor={processorOf(row)} />,
});

const ProcessorCell = ({ processor }: { processor: ConnectedTenant["personalDataController"] }) => {
  const { paths } = usePrivacyNavigate();
  if (!processor) return null;

  return (
    <DynamicGrid.Cell>
      <DynamicShared.Link
        Icon={TenantIcon3}
        to={paths.pdcInline(processor.id)}
        onClick={() => trackEvent.navigationCellClickWithinRegistrationGrid("personal-data-controllers")}
      >
        {processor.name}
      </DynamicShared.Link>
    </DynamicGrid.Cell>
  );
};

const responsibleColumn = ({ headerName }: { headerName: string }): GridColDef<GridViewRegistration> => ({
  ...DynamicGrid.requiredColDef({ headerName, field: REGISTRATION_METADATA_FIELDS.RESPONSIBLE }),
  width: 280,
  valueGetter: (_, row) => `${row.responsible.firstName} ${row.responsible.lastName}`,
  renderCell: ({ row }) => {
    return (
      <DynamicGrid.Cell withExtraSpacingTop={false}>
        <DynamicGrid.TextCellContent
          value={`${row.responsible.firstName} ${row.responsible.lastName}`}
          Icon={PersonIcon3}
        />
      </DynamicGrid.Cell>
    );
  },
});

const usersWithAccessColumn = ({ headerName }: { headerName: string }): GridColDef<GridViewRegistration> => ({
  ...DynamicGrid.requiredColDef({ headerName, field: REGISTRATION_METADATA_FIELDS.USERS_WITH_ACCESS }),
  width: 180,
  valueGetter: (_, row) => row.numberOfUsersWithAccess,
  renderCell: ({ row }) => (
    <DynamicGrid.Cell>
      <UsersWithAccessCell registrationId={row.registrationId} numberOfUsersWithAccess={row.numberOfUsersWithAccess} />
    </DynamicGrid.Cell>
  ),
});

const UsersWithAccessCell: FC<{ registrationId: string; numberOfUsersWithAccess: number }> = ({
  registrationId,
  numberOfUsersWithAccess,
}) => {
  const variant = useRegistrationVariant();
  const { paths } = usePrivacyNavigate();
  return (
    <DynamicGrid.OpenSubDrawerLink
      to={paths.registrationsUsersWithAccess({ id: registrationId, variant })}
      label={numberOfUsersWithAccess}
      Icon={PersonIcon3}
    />
  );
};

const dpiaColumn = (t: (translation: TranslationKey) => string): GridColDef<GridViewRegistration> => ({
  ...DynamicGrid.requiredColDef({ headerName: t("Dpia.Grid.Title"), field: REGISTRATION_METADATA_FIELDS.DPIA_STATUS }),
  valueGetter: (_, row) => (row.dpiaStatus ? t(`RegistrationDrawer.Dpia.${row.dpiaStatus}`) : ""),
  width: 250,
  renderCell: ({ row }) => (
    <DynamicGrid.Cell>
      <DpiaCell row={row} />
    </DynamicGrid.Cell>
  ),
});

const DpiaCell: FC<{ row: GridViewRegistration }> = ({ row }) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const currentOrganisation = useCurrentOrganisation();
  const dpiasQuery = useGetDpias({ enabled: currentOrganisation.dpiaEnabled });
  const { popoverProps, anchorProps } = useHoverPopover();
  const variant = useRegistrationVariant();
  const { paths } = usePrivacyNavigate();

  const openSubDrawerLink = (
    <DynamicGrid.OpenSubDrawerLink
      to={paths.registrationsDpiaRecommended({ id: row.registrationId, variant })}
      label={row.dpiaStatus ? t(`RegistrationDrawer.Dpia.${row.dpiaStatus}`) : undefined}
      Icon={DpiaIcon3}
    />
  );

  const connectedDpia = dpiasQuery.data?.find(dpia => dpia.id === row.connectedDpiaId);

  if (connectedDpia && hasPermission("nova-forms-api:dpia:read")) {
    return (
      <>
        <HoverPopover {...popoverProps}>
          <Stack spacing={1} sx={{ p: 4, maxWidth: "20rem" }}>
            <Typography
              variant="body2"
              sx={{
                lineHeight: 1,
                fontSize: theme => theme.typography.pxToRem(12),
                textWrap: "balance",
                whiteSpace: "pre-wrap",
              }}
            >
              <DynamicShared.Link Icon={DpiaIcon3} to={paths.dpiaById(connectedDpia.id)}>
                {t("RegistrationDrawer.Dpia.GoToConnectedDpia")}
              </DynamicShared.Link>
            </Typography>
          </Stack>
        </HoverPopover>
        <Box {...anchorProps}>{openSubDrawerLink}</Box>
      </>
    );
  }

  return openSubDrawerLink;
};

const sharedFromCustomerColumn = (t: (translation: TranslationKey) => string): GridColDef<GridViewRegistration> => ({
  ...DynamicGrid.requiredColDef({
    headerName: t("RegistrationGrid.SharedFromCustomerHeader"),
    field: IS_SHARED_FROM_CUSTOMER_FIELD,
  }),
  width: 200,
  filterOperators: [DynamicGrid.yesOrNoFilterOperator],
  valueGetter: (_, row) => t(DynamicForm.yesOrNoInfo[row.isSharedFromCustomer ? "yes" : "no"].labelKey),
  renderCell: ({ row }) => (
    <DynamicGrid.Cell withExtraSpacingTop={false}>
      <DynamicForm.YesOrNoInputSelectedValue value={row.isSharedFromCustomer ? "yes" : "no"} />
    </DynamicGrid.Cell>
  ),
});

export const registrationsGridPageRoutes = (variant: RegistrationVariant) =>
  DynamicGrid.setupRoutes({
    gridElement: <RegistrationsGridPage variant={variant} />,
    rowDrawerRoutes: [
      {
        index: true,
        element: <RegistrationInlineFormDrawer />,
        loader: ({ params }) =>
          void prefetchRegistrationUsersWithAccessCount(DynamicGrid.selectedRowIdFromParams(params), variant),
      },
      { path: "guided/*", element: <RegistrationMultiStepFormDrawer /> },
      { path: "history", element: <RegistrationHistoryDrawer /> },
      { path: "users-with-access", element: <UsersWithAccessSubDrawer /> },
      { path: "dpia-recommended", element: <DpiaRecommendedSubDrawer /> },
    ],
    gridPrefetcher: () => prefetchRegistrationsGridViewAndForm(variant),
    rowDrawerDefaultPrefetcher: ({ rowId }) => void prefetchRegistrationById(rowId, variant),
  });

export const prefetchRegistrationsGridViewAndForm = (variant: RegistrationVariant) => {
  prefetchRegistrationsGridView(variant);
  prefetchRegistrationForm(variant);
};
